import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined, LinkOutlined, ReloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { profileService } from "services/profileService";
import { useAuth } from "contexts/AuthContext";
import { formatEnumLabel } from "../constants";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

function FieldBlock({ label, value }) {
  return (
    <div style={{ padding: "6px 0" }}>
      <Typography.Text
        type="secondary"
        style={{ fontSize: 11, display: "block", marginBottom: 4 }}
      >
        {label}
      </Typography.Text>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{value}</div>
    </div>
  );
}

export default function AttachServicePage() {
  const { serviceCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";

  const [loading, setLoading] = useState(false);
  const [service, setService] = useState(null);
  const [error, setError] = useState(null);

  const [targetType, setTargetType] = useState("ENTITY");
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationLocationId, setOrganizationLocationId] = useState(null);

  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locations, setLocations] = useState([]);

  const loadService = async () => {
    if (!serviceCode) return;
    setLoading(true);
    setError(null);
    try {
      const page = await profileService.organizations.services.list({ page: 0, size: 500 });
      const offerings = Array.isArray(page?.content) ? page.content : [];
      const found = offerings.find(
        (o) => String(o?.code || "").toLowerCase() === String(serviceCode).toLowerCase(),
      );
      if (!found?.id) {
        throw new Error(`Service offering not found for code: ${serviceCode}`);
      }
      setService(found);
    } catch (err) {
      const message = toMessage(err, "Failed to load service offering");
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    setOrganizationsLoading(true);
    try {
      const res = await profileService.organizations.list({ page: 0, size: 200 });
      setOrganizations(Array.isArray(res?.content) ? res.content : []);
    } catch (err) {
      toast.error(toMessage(err, "Failed to load organizations"));
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const loadLocations = async (orgId) => {
    if (!orgId) {
      setLocations([]);
      return;
    }
    setLocationsLoading(true);
    try {
      const res = await profileService.organizations.locations.list(orgId, {
        page: 0,
        size: 200,
      });
      setLocations(Array.isArray(res?.content) ? res.content : []);
    } catch (err) {
      toast.error(toMessage(err, "Failed to load organization locations"));
    } finally {
      setLocationsLoading(false);
    }
  };

  useEffect(() => {
    loadService();
    loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCode]);

  useEffect(() => {
    if (targetType !== "LINE") return;
    loadLocations(organizationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, organizationId]);

  const organizationOptions = useMemo(
    () =>
      organizations.map((e) => ({
        value: e.id,
        label: e.name,
      })),
    [organizations],
  );

  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        value: l.id,
        label: l.name,
      })),
    [locations],
  );

  const canAttach =
    Boolean(service?.id) &&
    Boolean(organizationId) &&
    (targetType === "ENTITY" || Boolean(organizationLocationId));

  const attach = async () => {
    if (!canAttach) return;
    setLoading(true);
    try {
      if (targetType === "ENTITY") {
        await profileService.organizations.services.organizationServices.add(
          organizationId,
          service.id,
          actorId,
        );
      } else {
        await profileService.organizations.services.organizationLocationServices.add(
          organizationId,
          organizationLocationId,
          service.id,
          actorId,
        );
      }
      toast.success("Service attached successfully");
      navigate(paths.serviceOfferings);
    } catch (err) {
      toast.error(toMessage(err, "Failed to attach service"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="link"
          title="Attach Service Offering"
          subtitle="Attach a service offering to an organization or organization location"
          className="mb-2 mt-2 flex-1"
        />
        <Space wrap style={{ justifyContent: "flex-end" }}>
          <Button
            type="primary"
            onClick={() => navigate(paths.serviceOfferings)}
          >
           Go Back
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => { loadService(); loadOrganizations(); }}>
            Refresh
          </Button>
        </Space>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={loadService}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: 14,
              background: "#fff",
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <Typography.Text style={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>
                Service Details
              </Typography.Text>
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                Review the service offering you're attaching.
              </Typography.Text>
            </div>
            <div style={{ height: 1, background: "#eef2f7", margin: "12px 0" }} />
            <FieldBlock label="Name" value={service?.name || "-"} />
            <FieldBlock label="Code" value={service?.code || "-"} />
            <FieldBlock
              label="Category"
              value={service?.category ? formatEnumLabel(service.category) : "-"}
            />
            <FieldBlock
              label="Status"
              value={
                <Tag color={service?.active ? "green" : "red"} style={{ marginInlineEnd: 0 }}>
                  {service?.active ? "ACTIVE" : "INACTIVE"}
                </Tag>
              }
            />
          </div>
        </Col>

        <Col xs={24} md={12}>
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: 14,
              background: "#fff",
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <Typography.Text style={{ fontWeight: 800, fontSize: 14, color: "#111827" }}>
                Attach Target
              </Typography.Text>
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                Choose an organization or specific organization location.
              </Typography.Text>
            </div>
            <div style={{ height: 1, background: "#eef2f7", margin: "12px 0" }} />
            <Row gutter={[12, 12]}>
              <Col xs={24}>
                <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                  Target
                </Typography.Text>
                <Select
                  value={targetType}
                  onChange={(val) => {
                    setTargetType(val);
                    setOrganizationLocationId(null);
                  }}
                  options={[
                    { value: "ENTITY", label: "Organization" },
                    { value: "LINE", label: "Organization Location" },
                  ]}
                  style={{ width: "100%" }}
                />
              </Col>

              <Col xs={24}>
                <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                  Organization
                </Typography.Text>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select organization"
                  loading={organizationsLoading}
                  value={organizationId}
                  onChange={(val) => {
                    setOrganizationId(val);
                    setOrganizationLocationId(null);
                  }}
                  options={organizationOptions}
                  style={{ width: "100%" }}
                />
              </Col>

              {targetType === "LINE" ? (
                <Col xs={24}>
                  <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                    Organization Location
                  </Typography.Text>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={organizationId ? "Select location" : "Select organization first"}
                    loading={locationsLoading}
                    disabled={!organizationId}
                    value={organizationLocationId}
                    onChange={setOrganizationLocationId}
                    options={locationOptions}
                    style={{ width: "100%" }}
                  />
                </Col>
              ) : null}

              <Col xs={24} style={{ textAlign: "right" }}>
                <div style={{ height: 1, background: "#eef2f7", margin: "12px 0 12px" }} />
                <Space wrap style={{ justifyContent: "flex-end", width: "100%" }}>
                  <Button onClick={() => { setOrganizationId(null); setOrganizationLocationId(null); setTargetType("ENTITY"); }}>
                    Reset
                  </Button>
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    loading={loading}
                    disabled={!canAttach}
                    onClick={attach}
                  >
                    Attach
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
}
